import { toast } from "react-toastify";
import { config } from "../../config/environment";
import { 
  setLoading, 
  setError, 
  setLeadData,
  setOnboardingFlowId,
  completeStep,
  nextStep,
  LeadData
} from "./reducer";

// API client not needed - using fetch directly

// Create lead and start onboarding flow
export const createLead = (leadData: LeadData) => async (dispatch: any) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));

    // Create the onboarding flow (this will create the lead internally and send verification email)
    const flowResponse = await fetch(config.api.onboardingFlows, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: leadData.email,
        source: 'Website',
        metadata: {
          organizationName: leadData.organizationName,
          firstName: leadData.firstName,
          lastName: leadData.lastName,
          jobTitle: leadData.jobTitle,
          phoneNumber: leadData.phoneNumber,
          companySize: leadData.companySize,
        },
      }),
    });

    if (!flowResponse.ok) {
      const errorData = await flowResponse.json();
      const errorMessage = errorData.message || 'Failed to create onboarding flow';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    const flowResult = await flowResponse.json();
    console.log('Onboarding flow created:', flowResult);

    // Store lead data and onboarding flow ID
    dispatch(setLeadData(leadData));
    dispatch(setOnboardingFlowId(flowResult.id));
    dispatch(completeStep('lead-capture'));
    dispatch(nextStep());

    // Store in localStorage for session persistence
    localStorage.setItem('leadData', JSON.stringify(leadData));
    localStorage.setItem('onboardingFlowId', flowResult.id);

    return { onboardingFlow: flowResult };

  } catch (error: any) {
    const errorMessage = error.message || 'Something went wrong. Please try again.';
    dispatch(setError(errorMessage));
    toast.error(errorMessage);
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Create checkout session for payment
export const createCheckoutSession = (checkoutData: {
  planSlug: string;
  seatCount: number;
  billingCycle: 'MONTHLY' | 'YEARLY';
  onboardingFlowId: string;
  successUrl: string;
  cancelUrl: string;
  email: string;
  provider?: 'stripe' | 'razorpay';
}) => async (dispatch: any, getState: any) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));
    
    // Transform data to match backend DTO
    const payload = {
      email: checkoutData.email,
      planName: checkoutData.planSlug,
      seatCount: checkoutData.seatCount,
      billingCycle: checkoutData.billingCycle,
      onboardingFlowId: checkoutData.onboardingFlowId,
      successUrl: checkoutData.successUrl,
      cancelUrl: checkoutData.cancelUrl,
      provider: checkoutData.provider || 'stripe',
      // Let backend determine currency based on provider (USD for Stripe, INR for Razorpay)
    };
    
    console.log('Creating checkout session with payload:', payload);
    
    const response = await fetch(config.api.checkout, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const result = await response.json();
      
      // Store selected plan info
      localStorage.setItem('selectedPlan', JSON.stringify({
        plan: checkoutData.planSlug,
        seatCount: checkoutData.seatCount,
        billingCycle: checkoutData.billingCycle,
      }));
      
      return result;
    } else {
      const errorData = await response.json();
      const errorMessage = errorData.message || 'Failed to create payment session';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  } catch (error: any) {
    const errorMessage = error.message || 'Something went wrong with payment setup';
    dispatch(setError(errorMessage));
    toast.error(errorMessage);
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Load stored onboarding data
export const loadStoredOnboardingData = () => (dispatch: any) => {
  try {
    const storedLeadData = localStorage.getItem('leadData');
    const storedFlowId = localStorage.getItem('onboardingFlowId');
    
    if (storedLeadData) {
      const leadData = JSON.parse(storedLeadData);
      dispatch(setLeadData(leadData));
    }
    
    if (storedFlowId) {
      dispatch(setOnboardingFlowId(storedFlowId));
    }
  } catch (error) {
    console.warn('Failed to load stored onboarding data:', error);
  }
};

// Clear onboarding data
export const clearOnboardingData = () => (dispatch: any) => {
  localStorage.removeItem('leadData');
  localStorage.removeItem('onboardingFlowId');
  localStorage.removeItem('selectedPlan');
  localStorage.removeItem('preSelectedPlanId');
  localStorage.removeItem('initialLeadData');
};